import { useMemo, useState } from 'react';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { confirmAction } from '../../lib/confirm-service';
import { useTableSort } from '../../lib/table-sort';
import { useToast } from '../../lib/toast';
import { useAddOpdRegistrationRefund, useOpdPatientBills } from './opd.queries';
import { currency, formatOpdDate, formatOpdDateTime } from './opd.utils';
import type { OpdPaymentMode, OpdRegistration } from './types';

const paymentModes: OpdPaymentMode[] = ['Cash', 'UPI', 'Transfer to Bank Account', 'Cheque', 'Other', 'Online'];

interface MergedRefund {
  key: string;
  billNumber: string;
  date: string;
  mode: OpdPaymentMode;
  bankName: string | null;
  refundedBy: string;
  amount: number;
}

const RefundForm = ({ row }: { row: OpdRegistration }) => {
  const [mode, setMode] = useState<OpdPaymentMode>('Cash');
  const [bankName, setBankName] = useState('');
  const [amount, setAmount] = useState(0);
  const mutation = useAddOpdRegistrationRefund();
  const { showToast } = useToast();
  const creditAmount = row.billing.creditAmount;

  if (creditAmount <= 0) {
    return <EmptyState title="No credit on this bill" message="A refund can be recorded only when the selected OPD bill has a credit amount." />;
  }

  const submit = async () => {
    if (amount <= 0 || amount > creditAmount) return;
    const confirmed = await confirmAction({
      title: 'Confirm Refund',
      description: `Are you sure you want to record a refund of ${currency(amount)} for bill #${row.billing.billNumber}?`,
      confirmLabel: 'Yes, Record Refund',
      tone: 'danger',
    });
    if (!confirmed) return;
    mutation.mutate({ id: row.id, payload: { mode, bankName: bankName.trim() || null, amount } }, {
      onSuccess: () => {
        setAmount(0);
        setBankName('');
        showToast('Refund saved successfully.', 'success');
      },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The refund could not be recorded.', 'error'),
    });
  };

  return (
    <section className="opd-bill-subsection">
      <h3>Record a refund</h3>
      <div className="ipd-daycare-admission-row">
        <label>
          <span className="ipd-daycare-label-text">Bill</span>
          <input value={`Bill #${row.billing.billNumber} - Credit ${currency(creditAmount)}`} disabled />
        </label>
        <label><span className="ipd-daycare-label-text">Mode</span><select value={mode} onChange={(event) => setMode(event.target.value as OpdPaymentMode)}>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label><span className="ipd-daycare-label-text">Bank</span><input value={bankName} onChange={(event) => setBankName(event.target.value)} /></label>
        <label><span className="ipd-daycare-label-text">Amount</span><input type="number" min={0} max={creditAmount} step="0.01" value={amount} onChange={(event) => setAmount(Number(event.target.value))} /></label>
      </div>
      <div className="opd-form-actions">
        <button className="button button-primary" type="button" disabled={mutation.isPending} onClick={() => void submit()}>{mutation.isPending ? 'Saving...' : 'Refund'}</button>
      </div>
    </section>
  );
};

export const OpdAllRefundList = ({ row, showForm = false }: { row: OpdRegistration; showForm?: boolean }) => {
  const uhid = row.patientSnapshot.uhid;
  const opdQuery = useOpdPatientBills(uhid);
  const refunds = useMemo<MergedRefund[]>(() => (opdQuery.data?.data ?? []).flatMap((bill) => (bill.refunds ?? []).map((refund) => ({
    key: `opd-${bill.id}-${refund.id}`,
    billNumber: bill.billing.billNumber,
    date: refund.date,
    mode: refund.mode,
    bankName: refund.bankName,
    refundedBy: refund.createdBy.name,
    amount: refund.amount,
  }))).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()), [opdQuery.data]);
  const tableSort = useTableSort(refunds, {
    billNo: (refund) => refund.billNumber,
    date: (refund) => refund.date,
    mode: (refund) => refund.mode,
    bank: (refund) => refund.bankName,
    refundedBy: (refund) => refund.refundedBy,
    amount: (refund) => refund.amount,
  });

  if (!uhid) return <EmptyState title="No UHID on file" message="This patient has no UHID assigned, so their refund history can't be looked up." />;
  if (opdQuery.isLoading) return <LoadingState />;
  if (opdQuery.isError) return <ErrorState title="Unable to load refunds" message={opdQuery.error instanceof ApiError ? opdQuery.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void opdQuery.refetch() }} />;

  const totalRefunded = refunds.reduce((sum, refund) => sum + refund.amount, 0);

  return (
    <>
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>{showForm ? 'Refund' : 'All Refund'} - {row.patientSnapshot.name}</span></div>
        <p>{showForm ? `Bill #${row.billing.billNumber} | Bill Date : ${formatOpdDate(row.appointmentDate)} | Credit ${currency(row.billing.creditAmount)}` : `UHID ${uhid} | ${refunds.length} refund${refunds.length === 1 ? '' : 's'}`}</p>
      </header>
      {showForm ? <RefundForm row={row} /> : null}
      <section className="opd-bill-subsection">
        <div className="opd-bill-subsection-head"><h3>Refunds across OPD bills</h3><span>Total Refunded <strong>{currency(totalRefunded)}</strong></span></div>
        {refunds.length === 0 ? (
          <p className="opd-bill-empty-note">No refunds have been recorded for this patient.</p>
        ) : (
          <div className="table-scroll">
            <table className="opd-bill-payment-table">
              <thead><tr><th>#</th><SortableTh control={tableSort} column="billNo">Bill No</SortableTh><SortableTh control={tableSort} column="date">Date</SortableTh><SortableTh control={tableSort} column="mode">Mode</SortableTh><SortableTh control={tableSort} column="bank">Bank</SortableTh><SortableTh control={tableSort} column="refundedBy">Refund By</SortableTh><SortableTh control={tableSort} column="amount">Amount</SortableTh></tr></thead>
              <tbody>
                {tableSort.rows.map((refund, index) => (
                  <tr key={refund.key}>
                    <td>{index + 1}</td>
                    <td>{refund.billNumber}</td>
                    <td>{formatOpdDateTime(refund.date)}</td>
                    <td>{refund.mode}</td>
                    <td>{refund.bankName ?? '-'}</td>
                    <td>{refund.refundedBy}</td>
                    <td>{currency(refund.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};
